import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Crown, CreditCard, Calendar, Receipt } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function Billing() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);

  const load = () => api.get("/billing/me").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const cancel = async () => {
    if (!window.confirm("Cancel your Premium subscription? You'll keep access until the end of your billing period.")) return;
    try { await api.post("/billing/cancel"); await refresh(); toast.success("Subscription set to cancel at period end."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const sub = data?.subscription;
  const active = sub && sub.status === "active";

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-head text-4xl font-black tracking-tighter mb-8">Membership & Billing</h1>

      <div className="glass rounded-3xl p-8 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${active ? "bg-neon-pink/20 glow-pink" : "bg-white/10"}`}><Crown className={`w-6 h-6 ${active ? "text-neon-pink" : "text-muted-foreground"}`} /></div>
            <div>
              <p className="font-head text-xl font-bold" data-testid="plan-name">{active ? "Premium" : "Free"}</p>
              <p className="text-sm text-muted-foreground capitalize">{active ? `${sub.plan} plan · ${sub.cancel_at_period_end ? "cancels at period end" : "active"}` : "2 free recordings"}</p>
            </div>
          </div>
          {!active && <Button onClick={() => nav("/premium")} className="rounded-full bg-neon-pink hover:bg-neon-pink/90 font-bold">Upgrade</Button>}
        </div>

        {active && (
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div className="card-surface rounded-xl p-4"><p className="text-muted-foreground flex items-center gap-2 mb-1"><Calendar className="w-4 h-4" /> Renewal date</p><p className="font-semibold">{sub.renewal_date ? new Date(sub.renewal_date).toLocaleDateString() : "—"}</p></div>
            <div className="card-surface rounded-xl p-4"><p className="text-muted-foreground flex items-center gap-2 mb-1"><CreditCard className="w-4 h-4" /> Payment method</p><p className="font-semibold">{sub.card_last4 ? `Visa •••• ${sub.card_last4}` : "Card on file"}</p></div>
          </div>
        )}

        {active && (
          <div className="flex gap-3 mt-6">
            {!sub.cancel_at_period_end && <Button onClick={cancel} variant="outline" className="rounded-full glass border-white/20" data-testid="cancel-sub">Cancel subscription</Button>}
            <Button onClick={() => nav("/premium")} variant="ghost" className="rounded-full">Change plan</Button>
          </div>
        )}
      </div>

      <div className="card-surface rounded-2xl p-6">
        <h3 className="font-head font-bold mb-4 flex items-center gap-2"><Receipt className="w-5 h-5" /> Billing history</h3>
        {!data?.payments?.length ? <p className="text-sm text-muted-foreground">No payments yet.</p> : (
          <div className="divide-y divide-white/5">
            {data.payments.map((p, i) => (
              <div key={i} className="flex items-center justify-between py-3 text-sm">
                <div><p className="font-semibold capitalize">{p.plan} plan</p><p className="text-xs text-muted-foreground">{new Date(p.created_at).toLocaleString()}</p></div>
                <div className="text-right"><p className="font-mono">CA${p.amount?.toFixed(2)}</p><p className="text-xs text-green-400">Paid</p></div>
              </div>
            ))}
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground mt-4 text-center">SINGVI never stores your raw card number. Payments are handled securely by Stripe.</p>
    </div>
  );
}
